import {
  SelectMenu,
  Typography,
  Checkbox,
  CheckboxMenu,
  TextArea,
  TextField,
} from '@scale/scaleui';

import {
  BillingFlags,
  BillingFlagsOptionsDescriptions,
  ProjectVisibilityType,
  BillableProjectVisibilityTypes,
  ExposedBillingFlagsOptionsType,
  HourlyWorkTypes,
  ProjectWorkUnit,
  LanguageCode,
} from '@scale/shared/types/projectParameterGroup';
import React, { useEffect, useState } from 'react';
import { SLAPicker } from 'react-scale/corp/components/QM/billing/slaDropdown';
import SelectCustomer from 'react-scale/corp/components/UI/SelectCustomer';
import SelectPodWorkflow from 'react-scale/corp/components/UI/SelectWorkflow/SelectPodWorkflow';
import SelectHubstaff from 'react-scale/corp/components/UI/SelectHubstaff/SelectHubstaff';
import { SelectProjects } from 'react-scale/corp/components/UI/SelectProjects';
import {
  PROJECT_BILLING_TYPE_OPTIONS,
  PROJECT_WORK_UNIT_OPTIONS,
  BILLING_FLAG_OPTIONS,
  PROJECT_BILLING_PILOT_TYPES,
  LANGUAGE_CODE_OPTIONS,
} from 'react-scale/corp/views/CorpProjectGroupManager/views/Billing/utils/constants';
import { BillingApprovalWarningBanner } from 'react-scale/corp/views/CorpProjectGroupManager/views/Billing/components/Banners/BillingApprovalWarningBanner';
import { SelectOption } from 'react-scale/corp/components/UI/Select';

interface NewProjectBillingFormProps {
  setHourlyWorkTypes: (_: HourlyWorkTypes[]) => void;
  hourlyWorkTypes: string[];
  projectBillingType: ProjectVisibilityType;
  setProjectBillingType: (projectBillingType: ProjectVisibilityType) => void;
  internalForCustomer?: { label: string; value: string };
  setInternalForCustomer: (internalForCustomer?: { label: string; value: string }) => void;
  opsWorkflowPod?: { label: string; value: string };
  setOpsWorkflowPod: (_?: { label: string; value: string }) => void;
  projectWorkUnit: ProjectWorkUnit;
  setProjectWorkUnit: (_: ProjectWorkUnit) => void;
  projectHubstaff?: { label: string; value: string } | undefined;
  setProjectHubstaff?: (_?: { label: string; value: string }) => void;
  projectLanguage?: LanguageCode /** @deprecated field, use languageCodes instead */;
  setProjectLanguage?: (
    _: LanguageCode,
  ) => void /** @deprecated field, use setLanguageCodes instead */;
  languageCodes?: LanguageCode[];
  setLanguageCodes?: (_: LanguageCode[]) => void;
  billingSla?: string;
  setBillingSla: (slaId?: string) => void;
  customer?: { label: string; value: string };
  taskType: string;
  createZeroSLA: boolean;
  setCreateZeroSLA: (createZeroSLA: boolean) => void;
  projectBillingFlags?: BillingFlags[];
  setProjectBillingFlags?: (projectBillingFlags: BillingFlags[]) => void;
  setNoSlaReason?: (_: string) => void;
  noSlaReason?: string;
  newSlaTicket?: string;
  setNewSlaTicket?: (_: string) => void;
  bypassBillingSla?: boolean;
  linkedProjects?: SelectOption[];
  setLinkedProjects?: (linkedProjects: SelectOption[]) => void;
  isNewSetupPage?: boolean;
}

// eslint-disable-next-line complexity
const NewProjectBillingForm: React.FC<NewProjectBillingFormProps> = ({
  setHourlyWorkTypes,
  hourlyWorkTypes,
  projectBillingType,
  setProjectBillingType,
  internalForCustomer,
  setInternalForCustomer,
  billingSla,
  setBillingSla,
  customer,
  taskType,
  createZeroSLA,
  setCreateZeroSLA,
  projectBillingFlags,
  setProjectBillingFlags,
  opsWorkflowPod,
  setOpsWorkflowPod,
  projectWorkUnit,
  setProjectWorkUnit,
  projectHubstaff,
  setProjectHubstaff,

  /** @deprecated field, use languageCodes instead */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  projectLanguage,
  /** @deprecated field, use setLanguageCodes instead */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setProjectLanguage,

  languageCodes,
  setLanguageCodes,
  setNoSlaReason,
  noSlaReason,
  setNewSlaTicket,
  newSlaTicket,
  bypassBillingSla = false,
  linkedProjects,
  setLinkedProjects,
  isNewSetupPage,
}) => {
  const [selectedFlags, setSelectedFlags] = useState<{ id: string; value: string }[]>([]);

  useEffect(() => {
    const shouldBeZeroSLA = BillableProjectVisibilityTypes.includes(projectBillingType);
    if (!bypassBillingSla && shouldBeZeroSLA) {
      return;
    }
    setCreateZeroSLA(shouldBeZeroSLA);
  }, [bypassBillingSla, projectBillingType]);

  useEffect(() => {
    if (projectBillingType === ProjectVisibilityType.Customer) {
      setSelectedFlags([]);
    } else {
      setSelectedFlags(
        BILLING_FLAG_OPTIONS.filter(o =>
          [BillingFlags.isCapacityBuildingProject, BillingFlags.isTraining].includes(
            o.id as ExposedBillingFlagsOptionsType,
          ),
        ),
      );
    }
  }, [projectBillingType]);

  useEffect(() => {
    if (projectBillingType === ProjectVisibilityType.Internal) {
      setProjectWorkUnit(ProjectWorkUnit.None);
    } else {
      setProjectWorkUnit(ProjectWorkUnit.Task);
    }
  }, [projectBillingType]);

  useEffect(() => {
    setNoSlaReason && setNoSlaReason('');
  }, [projectBillingType]);

  useEffect(() => {
    if (projectBillingType !== ProjectVisibilityType.Customer && billingSla) setBillingSla('');
  }, [projectBillingType]);

  useEffect(() => {
    if (setProjectBillingFlags) {
      setProjectBillingFlags(selectedFlags.map(f => f.id) as BillingFlags[]);
    }
  }, [selectedFlags]);

  useEffect(() => {
    if (projectWorkUnit !== ProjectWorkUnit.Hour && hourlyWorkTypes?.length !== 0) {
      setHourlyWorkTypes([]);
    }
  }, [hourlyWorkTypes?.length, projectWorkUnit, setHourlyWorkTypes]);

  useEffect(() => {
    const hasHourlyWorkTypes = hourlyWorkTypes?.length;
    const hasHubstaffType = hourlyWorkTypes?.some(hwt => hwt === HourlyWorkTypes.Hubstaff);

    if (projectWorkUnit !== ProjectWorkUnit.Hour || !hasHourlyWorkTypes || !hasHubstaffType) {
      setProjectHubstaff?.(undefined);
    }
  }, [hourlyWorkTypes, projectWorkUnit]);

  useEffect(() => {
    if (newSlaTicket && billingSla && setNewSlaTicket) setNewSlaTicket('');
  }, [billingSla]);

  useEffect(() => {
    if (!createZeroSLA) {
      setNoSlaReason && setNoSlaReason('');
    }
    if (createZeroSLA) setBillingSla('');
  }, [createZeroSLA]);

  useEffect(() => {
    if (projectBillingType !== ProjectVisibilityType.InternalForCustomer && internalForCustomer) {
      setInternalForCustomer(undefined);
    }
  }, [internalForCustomer, projectBillingType, setInternalForCustomer]);

  return (
    <div className="scaleui scale-theme scale-tailwind">
      {isNewSetupPage && <Typography.H4>Initial Project Billing Setup</Typography.H4>}
      <div className="mt-4">
        <Typography.Paragraph1>Where is the data going?</Typography.Paragraph1>
        <SelectMenu
          className="w-[550px]"
          selectedOptionId={projectBillingType}
          setSelected={(value: string) => setProjectBillingType(value as ProjectVisibilityType)}
          options={PROJECT_BILLING_TYPE_OPTIONS}
        />
        {PROJECT_BILLING_PILOT_TYPES.includes(projectBillingType) && (
          <Typography.Paragraph2 className="mb-2">
            Pilots refer to pilot projects done outside of the production contract. These are
            generally run by the Pilots team.
          </Typography.Paragraph2>
        )}
        {projectBillingType && (
          <Typography.Paragraph2 className="font-bold">
            {`This Project is ${
              BillableProjectVisibilityTypes.includes(projectBillingType) ? '' : 'Not '
            }Billable.`}
          </Typography.Paragraph2>
        )}
        {projectBillingType === ProjectVisibilityType.Linked &&
          linkedProjects &&
          setLinkedProjects && (
            <div className="mt-4">
              <Typography.Paragraph1>Which project is this linked to?</Typography.Paragraph1>
              <SelectProjects
                placeholder="All Projects"
                selectedValues={linkedProjects}
                setSelectedValues={setLinkedProjects}
                taskType={taskType}
              />
            </div>
          )}
        {BillableProjectVisibilityTypes.includes(projectBillingType) && (
          <div className="mt-4">
            <Typography.Paragraph1>Select billing by hours, tasks, or model</Typography.Paragraph1>
            <SelectMenu
              className="w-[400px]"
              selectedOptionId={projectWorkUnit}
              setSelected={(value: string) => setProjectWorkUnit(value as ProjectWorkUnit)}
              options={PROJECT_WORK_UNIT_OPTIONS}
            />

            {projectWorkUnit === ProjectWorkUnit.Hour && (
              <div className="mt-4 w-[400px]">
                <Typography.Paragraph1>
                  Where does your project get its hours from?
                </Typography.Paragraph1>
                <SelectMenu
                  multiple
                  selectedOptionId={hourlyWorkTypes}
                  setSelected={value => setHourlyWorkTypes(value as HourlyWorkTypes[])}
                  options={Object.keys(HourlyWorkTypes).map(hourlyWorkType => {
                    return { id: hourlyWorkType, name: hourlyWorkType };
                  })}
                />
                {hourlyWorkTypes.some(hwt => hwt === HourlyWorkTypes.Hubstaff) && (
                  <div className="mt-4">
                    <Typography.Paragraph1>Which Hubstaff project is this?</Typography.Paragraph1>
                    <SelectHubstaff
                      className="w-[400px]"
                      value={projectHubstaff}
                      onChange={setProjectHubstaff}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {customer && taskType && projectBillingType === ProjectVisibilityType.InternalForCustomer && (
        <div className="mt-4">
          <Typography.Paragraph1>
            Which customer is this internal project for?
          </Typography.Paragraph1>
          <SelectCustomer
            style={{ width: 400 }}
            value={internalForCustomer}
            onChange={(x?: $FIXME) => {
              setInternalForCustomer(x);
            }}
          />
        </div>
      )}

      {!bypassBillingSla &&
        customer &&
        taskType &&
        projectBillingType === ProjectVisibilityType.Customer && (
          <div className="mt-4">
            <Typography.Paragraph1>Choose an SLA (prices are in cents)</Typography.Paragraph1>
            {!createZeroSLA && (
              <SLAPicker
                defaultSla={billingSla}
                onChange={(sla: string) => setBillingSla(sla)}
                taskType={taskType}
                taskCustomer={customer.value}
                expandInPlace={true}
              />
            )}
            <div>
              <Checkbox
                className="mt-4"
                id="createZeroSLA"
                onChange={() => setCreateZeroSLA(!createZeroSLA)}
                checked={createZeroSLA}
                label="The right SLA isn't shown here. Create a $0 SLA for me"
              />
            </div>
          </div>
        )}
      {!bypassBillingSla &&
      (createZeroSLA ||
        (projectBillingType && projectBillingType !== ProjectVisibilityType.Customer)) ? (
        <div className="mt-4 w-[400px]">
          <Typography.Paragraph2>
            Please enter a reason why you are not providing a billing SLA for this project.
          </Typography.Paragraph2>
          <TextArea
            id="zeroSLAReason"
            value={noSlaReason}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              setNoSlaReason && setNoSlaReason(e.target.value)
            }
          />
        </div>
      ) : (
        <></>
      )}
      {!bypassBillingSla && !billingSla ? (
        <div className="mt-4 w-[400px]">
          <Typography.Paragraph2>
            Do you need a new billing spec created for this project? If so, please fill out{' '}
            <a
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#2B99D8' }}
              href={'https://linear.app/scale-epd/team/REV/all'}
            >
              this form for RevOps
            </a>{' '}
            and link the ticket below.
          </Typography.Paragraph2>
          <TextField
            value={newSlaTicket}
            onChange={event => setNewSlaTicket && setNewSlaTicket(event.target.value)}
          />
        </div>
      ) : (
        <></>
      )}

      <div className="mt-4 w-[400px]">
        <Typography.Paragraph1>Ops Workflow Pod (required)</Typography.Paragraph1>
        <SelectPodWorkflow
          value={opsWorkflowPod?.value}
          fullWidth={true}
          onChange={setOpsWorkflowPod}
        />
      </div>
      <div className="mt-4 w-[400px]">
        <Typography.Paragraph1>Language Codes (required)</Typography.Paragraph1>
        <SelectMenu
          multiple
          placeholder="Select language(s)"
          className="w-[400px]"
          selectedOptionId={languageCodes || []}
          setSelected={(value: string[]) => setLanguageCodes?.(value.map(v => v as LanguageCode))}
          options={LANGUAGE_CODE_OPTIONS}
        />
        <Typography.Paragraph2>
          If you aren't sure what language(s) your project uses (ex. Coding), choose en_US.
        </Typography.Paragraph2>
      </div>
      {projectBillingFlags && setProjectBillingFlags && (
        <div className="mt-4">
          <Typography.Paragraph1>
            Does your project fit in any of these attributes?
          </Typography.Paragraph1>
          <CheckboxMenu
            options={BILLING_FLAG_OPTIONS}
            value={selectedFlags}
            onChange={setSelectedFlags}
          />
          <div style={{ marginTop: '10px' }} />
          <Typography.Paragraph2>Explanation of attributes</Typography.Paragraph2>
          <div>
            {Object.keys(BillingFlagsOptionsDescriptions).map(key => (
              <div key={key}>
                <strong style={{ marginRight: '5px' }}>
                  {BillingFlagsOptionsDescriptions[key as ExposedBillingFlagsOptionsType].name}
                </strong>
                {BillingFlagsOptionsDescriptions[key as ExposedBillingFlagsOptionsType].description}
              </div>
            ))}
          </div>
        </div>
      )}
      {bypassBillingSla && <BillingApprovalWarningBanner />}
    </div>
  );
};

export default NewProjectBillingForm;
